import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Home, PlusCircle, Bell, LogOut, Menu, X, Ticket } from 'lucide-react';
import api from '../services/api';
import io from 'socket.io-client';
import { SOCKET_URL, SOCKET_PATH } from '../config';

export default function CustomerLayout({ children }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showNotif, setShowNotif] = useState(false);
  const [mobileMenu, setMobileMenu] = useState(false);
  const notifRef = useRef(null);

  useEffect(() => {
    loadNotifications();
    const socket = io(SOCKET_URL, { path: SOCKET_PATH });
    socket.emit('joinRoom', { userId: user?.id });
    socket.on('notification', () => { loadNotifications(); });
    return () => socket.disconnect();
  }, [user]);

  useEffect(() => {
    function handleClick(e) {
      if (notifRef.current && !notifRef.current.contains(e.target)) setShowNotif(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const loadNotifications = async () => {
    try {
      const [notifRes, countRes] = await Promise.all([
        api.get('/notifications'),
        api.get('/notifications/unread-count')
      ]);
      setNotifications(notifRes.data.slice(0, 20));
      setUnreadCount(countRes.data.count);
    } catch (err) {}
  };

  const markAsRead = async (id) => {
    await api.put(`/notifications/${id}/read`);
    loadNotifications();
  };

  const markAllRead = async () => {
    await api.put('/notifications/read-all');
    loadNotifications();
  };

  const navItems = [
    { to: '/dashboard', icon: Home, label: 'Ana Sayfa' },
    { to: '/new-ticket', icon: PlusCircle, label: 'Yeni Talep' },
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Top Navbar */}
      <nav className="bg-white border-b border-slate-200 sticky top-0 z-50 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-3">
              <button onClick={() => setMobileMenu(!mobileMenu)} className="sm:hidden p-2 text-slate-600 hover:bg-slate-100 rounded-lg">
                {mobileMenu ? <X size={20} /> : <Menu size={20} />}
              </button>
              <Link to="/dashboard" className="flex items-center gap-2">
                <img src="/logo.png" alt="DSY" className="h-9" />
                <span className="font-bold text-lg text-slate-800 hidden sm:block">Destek Sistemi</span>
              </Link>
            </div>

            <div className="hidden sm:flex items-center gap-1">
              {navItems.map(item => (
                <Link key={item.to} to={item.to} className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${location.pathname === item.to ? 'bg-blue-50 text-blue-700' : 'text-slate-600 hover:bg-slate-100'}`}>
                  <item.icon size={18} />
                  {item.label}
                </Link>
              ))}
            </div>

            <div className="flex items-center gap-3">
              <div className="relative" ref={notifRef}>
                <button onClick={() => setShowNotif(!showNotif)} className="relative p-2 text-slate-600 hover:bg-slate-100 rounded-lg transition-colors">
                  <Bell size={20} />
                  {unreadCount > 0 && <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center font-bold">{unreadCount > 9 ? '9+' : unreadCount}</span>}
                </button>
                {showNotif && (
                  <div className="absolute right-0 mt-2 w-80 bg-white rounded-xl shadow-xl border border-slate-200 overflow-hidden z-50">
                    <div className="flex items-center justify-between px-4 py-3 bg-slate-50 border-b">
                      <span className="font-semibold text-sm text-slate-700">Bildirimler</span>
                      {unreadCount > 0 && <button onClick={markAllRead} className="text-xs text-blue-600 hover:underline">Tümünü okundu yap</button>}
                    </div>
                    <div className="max-h-72 overflow-y-auto">
                      {notifications.length === 0 ? (
                        <p className="text-center text-slate-400 py-8 text-sm">Bildirim yok</p>
                      ) : notifications.map(n => (
                        <Link key={n.Id} to={n.TicketId ? `/tickets/${n.TicketId}` : '#'} onClick={() => { markAsRead(n.Id); setShowNotif(false); }}
                          className={`block px-4 py-3 border-b border-slate-100 hover:bg-blue-50 transition-colors ${!n.IsRead ? 'bg-blue-50/50' : ''}`}>
                          <p className="text-sm font-medium text-slate-800">{n.Title}</p>
                          <p className="text-xs text-slate-500 mt-0.5">{n.Message}</p>
                          <p className="text-xs text-slate-400 mt-1">{new Date(n.CreatedAt).toLocaleString('tr-TR', { timeZone: 'Europe/Istanbul' })}</p>
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div className="flex items-center gap-2 pl-3 border-l border-slate-200">
                <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white text-sm font-bold">
                  {user?.fullName?.charAt(0) || 'U'}
                </div>
                <span className="text-sm font-medium text-slate-700 hidden sm:block">{user?.fullName}</span>
                <button onClick={logout} className="p-2 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors" title="Çıkış">
                  <LogOut size={18} />
                </button>
              </div>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile Menu */}
      {mobileMenu && (
        <div className="sm:hidden bg-white border-b border-slate-200 shadow-lg">
          {navItems.map(item => (
            <Link key={item.to} to={item.to} onClick={() => setMobileMenu(false)}
              className={`flex items-center gap-3 px-6 py-3 text-sm font-medium ${location.pathname === item.to ? 'bg-blue-50 text-blue-700' : 'text-slate-600'}`}>
              <item.icon size={18} />
              {item.label}
            </Link>
          ))}
        </div>
      )}

      {/* Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        {children}
      </main>
    </div>
  );
}
